/**
 * ID Scan debug logger — captures every stage of the scan flow
 * for debugging in the field. Per ID Scan Spec §12.
 *
 * Events are kept in-memory (last 100) and also flushed to the
 * console with a [id-scan] prefix so they show up in DevTools.
 *
 * The IDScanner UI exposes a "Show debug log" panel for failure
 * triage in the field.
 */

export type IDScanEvent =
  | 'scan_button_clicked'
  | 'camera_permission_requested'
  | 'camera_permission_granted'
  | 'camera_permission_denied'
  | 'video_stream_started'
  | 'video_stream_failed'
  | 'scan_loop_started'
  | 'scan_loop_stopped'
  | 'frame_processed'
  | 'barcode_detected'
  | 'decode_succeeded'
  | 'decode_failed'
  | 'parse_succeeded'
  | 'parse_partial'
  | 'parse_failed'
  | 'confirmation_opened'
  | 'patient_fields_filled'
  | 'patient_fields_skipped'
  | 'merge_conflict'
  | 'still_image_capture'
  | 'still_image_decode_attempted'
  | 'torch_toggled'
  | 'scan_cancelled'
  | 'scan_timeout'
  | 'camera_enumerate_failed'
  | 'camera_switched'
  | 'mirror_toggled'
  | 'auto_mirror_enabled'
  | 'camera_focus_applied'
  | 'camera_focus_no_capabilities'
  | 'camera_focus_failed'
  | 'tap_to_focus'
  | 'tap_to_focus_failed'
  | 'camera_remembered_failed'
  | 'camera_auto_switching';

export interface IDScanLogEntry {
  timestamp: string;
  event: IDScanEvent;
  detail?: Record<string, unknown>;
  scanSessionId: string;
}

let _sessionId: string | null = null;
const _entries: IDScanLogEntry[] = [];
const MAX_ENTRIES = 100;

const listeners = new Set<(entries: IDScanLogEntry[]) => void>();

export function startScanSession(): string {
  _sessionId = crypto.randomUUID();
  _entries.length = 0;
  log('scan_button_clicked', captureDeviceInfo());
  return _sessionId;
}

export function log(event: IDScanEvent, detail?: Record<string, unknown>): void {
  if (!_sessionId) _sessionId = crypto.randomUUID();
  const entry: IDScanLogEntry = {
    timestamp: new Date().toISOString(),
    event,
    detail,
    scanSessionId: _sessionId,
  };
  _entries.push(entry);
  if (_entries.length > MAX_ENTRIES) _entries.shift();

  // eslint-disable-next-line no-console
  console.log(`[id-scan] ${event}`, detail ?? '');

  // Notify subscribers (debug panel)
  listeners.forEach((l) => l([..._entries]));
}

export function getLog(): IDScanLogEntry[] {
  return [..._entries];
}

export function clearLog(): void {
  _entries.length = 0;
}

export function subscribeToLog(fn: (entries: IDScanLogEntry[]) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function captureDeviceInfo(): Record<string, unknown> {
  if (typeof navigator === 'undefined') return {};

  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    secureContext: typeof window !== 'undefined' ? window.isSecureContext : false,
    online: navigator.onLine,
    barcodeDetectorSupported: typeof (window as { BarcodeDetector?: unknown }).BarcodeDetector !== 'undefined',
    mediaDevicesSupported: typeof navigator.mediaDevices !== 'undefined',
    timestamp: new Date().toISOString(),
  };
}

export async function captureBarcodeDetectorFormats(): Promise<string[] | null> {
  type BarcodeDetectorCtor = { getSupportedFormats?: () => Promise<string[]> };
  const w = window as unknown as { BarcodeDetector?: BarcodeDetectorCtor };
  if (typeof w.BarcodeDetector === 'undefined') return null;
  try {
    return (await w.BarcodeDetector.getSupportedFormats?.()) ?? null;
  } catch {
    return null;
  }
}
